import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import moment from 'moment';
import { MdVpnKey } from 'react-icons/md';
import DefaultScreen from '../../DefaultScreen';
import { Title } from '../../../components/managment/Title';
import { BaseDialog } from '../../../components/DialogTemplates/BaseDialog';
import { logout } from '../../../helpers/Api/PulseemReactAPI';
import {
    getSsoSettings,
    saveSsoMetadataFile,
    saveSsoMetadataLink,
    saveSsoMode,
    saveSsoShare,
    saveSsoUsers,
    startSsoSetup,
} from '../../../redux/reducers/SsoSettingsSlice';

/**
 * Settings > SSO (SAML): the account Admin connects the company login (Microsoft Entra ID, Okta, Google Workspace, any SAML 2.0 IdP).
 * Same 4 parts as the Backoffice SSO popup. API: WebSiteAPI Controllers/Settings/SsoSettingsController.cs.
 * The SAML login itself is the web site's (/Sso/..., Login.aspx).
 */
const box: React.CSSProperties = { border: '1px solid var(--pls-d-border, #ddd)', borderRadius: 8, padding: '14px 18px', maxWidth: 980, marginTop: 16, background: 'var(--pls-d-surface, #fff)' };
const helpBox: React.CSSProperties = { background: 'var(--pls-d-surface-2, #f4f8fc)', border: '1px solid var(--pls-d-border, #c9dcef)', borderRadius: 8, padding: '12px 18px', maxWidth: 980, lineHeight: 1.6 };
const valueBox: React.CSSProperties = { fontFamily: 'Consolas, monospace', fontSize: 13, direction: 'ltr', background: 'var(--pls-d-surface-2, #f3f3f3)', border: '1px solid var(--pls-d-border, #ddd)', borderRadius: 4, padding: '1px 6px', wordBreak: 'break-all' };
const cell: React.CSSProperties = { borderBottom: '1px solid var(--pls-d-border-subtle, #eee)', padding: '6px 8px', textAlign: 'start', verticalAlign: 'top' };
const GUIDES = ['entra', 'okta', 'google', 'other'];

const SsoSettings = ({ classes }: any) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { isRTL, windowSize } = useSelector((state: any) => state.core);

    const [data, setData] = useState<any>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [link, setLink] = useState('');
    const [names, setNames] = useState<Record<string, string>>({});
    const [rowErrors, setRowErrors] = useState<Record<string, number>>({});
    const [mode, setMode] = useState('0');
    const [openGuide, setOpenGuide] = useState('');
    const [confirmShare, setConfirmShare] = useState<boolean | null>(null); // share on / off waiting for "are you sure"
    const [copied, setCopied] = useState('');

    const tr = (key: string, options?: any) => t(`settings.sso.${key}`, options) as any;

    // SubUsers (even Admins) only VIEW - only the main login may change SSO (the API answers 403 MAIN_LOGIN_ONLY)
    const readOnly = data?.CanEdit === false;
    const mainLogin = ((data?.Users || []).find((u: any) => u.UserType === 'Main login') || {}).UserName || '';

    const apply = (view: any) => {
        setData(view);
        setMode(`${view?.Mode ?? 0}`);
        setLink(view?.IdpMetadataUrl || '');
        const n: Record<string, string> = {};
        (view?.Users || []).forEach((u: any) => { n[u.UserName] = u.SsoNameId || ''; });
        setNames(n);
        setRowErrors({});
    };

    /** Calls the API: payload on 201, otherwise shows the translated error code and returns null. */
    const call = async (action: any) => {
        setBusy(true);
        setError('');
        setMessage('');
        try {
            const payload = ((await dispatch(action)) as any)?.payload;
            if (payload?.StatusCode === 401) {
                logout();
                return null;
            }
            if (payload?.StatusCode !== 201) {
                const key = `errors.${payload?.Message || 'general'}`;
                const text = tr(key);
                setError(text === `settings.sso.${key}` ? tr('errors.general') : text);
                return null;
            }
            return payload;
        } finally {
            setBusy(false);
        }
    };

    const load = async () => {
        const payload = await call(getSsoSettings());
        if (payload) apply(payload.Data);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onStart = async () => {
        const payload = await call(startSsoSetup({}));
        if (payload) apply(payload.Data);
    };

    const onReadLink = async () => {
        const payload = await call(saveSsoMetadataLink({ Url: link.trim() }));
        if (payload) { apply(payload.Data); setMessage(tr('step2.saved')); }
    };

    const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        if (file.size > 1024 * 1024) { setError(tr('errors.TOO_LARGE')); return; }
        const reader = new FileReader();
        reader.onload = async () => {
            const payload = await call(saveSsoMetadataFile({ Xml: String(reader.result || '') }));
            if (payload) { apply(payload.Data); setMessage(tr('step2.saved')); }
        };
        reader.readAsText(file);
    };

    const onFillEmails = () => {
        const n = { ...names };
        (data?.Users || []).forEach((u: any) => { if (!n[u.UserName] && u.Email) n[u.UserName] = u.Email; });
        setNames(n);
    };

    const onSaveUsers = async () => {
        const users = (data?.Users || []).map((u: any) => ({ UserName: u.UserName, SsoNameId: (names[u.UserName] || '').trim() }));
        const payload = await call(saveSsoUsers({ Users: users }));
        if (!payload) return;
        const failed: Record<string, number> = {};
        (payload.Data || []).forEach((r: any) => { if (r.Result !== 1) failed[r.UserName] = r.Result; });
        const reloaded = await call(getSsoSettings());
        if (reloaded) apply(reloaded.Data);
        setRowErrors(failed);
        if (Object.keys(failed).length === 0) setMessage(tr('step3.saved'));
        else setError(tr('step3.someNotSaved'));
    };

    const onSaveMode = async () => {
        const payload = await call(saveSsoMode({ Mode: parseInt(mode, 10) }));
        if (payload) { apply(payload.Data); setMessage(tr('step4.saved')); }
    };

    const copy = (key: string, value: string) => {
        navigator.clipboard.writeText(value);
        setCopied(key);
        setTimeout(() => setCopied(''), 2000);
    };

    const button = (text: string, onClick: () => void, disabled = false) => (
        <Button className={clsx(classes.btn, classes.btnRounded)} style={{ marginInlineEnd: 10, marginTop: 6 }} disabled={busy || disabled} onClick={onClick}>
            {text}
        </Button>
    );

    /** A guide step: %ENTITY_ID% / %ACS_URL% / %APP_NAME% become boxed values. */
    const renderWithValues = (text: string) => {
        const values: Record<string, string> = { '%ENTITY_ID%': data?.EntityId, '%ACS_URL%': data?.AcsUrl, '%APP_NAME%': 'Pulseem' };
        return text.split(/(%ENTITY_ID%|%ACS_URL%|%APP_NAME%)/).map((part, i) =>
            values[part] !== undefined ? <span key={i} style={valueBox}>{values[part]}</span> : <React.Fragment key={i}>{part}</React.Fragment>);
    };

    const copyRow = (key: string, label: string, value: string) => (
        <Box style={{ display: windowSize !== 'xs' ? 'flex' : 'block', alignItems: 'center', margin: '8px 0' }}>
            <Typography style={{ minWidth: 230, fontWeight: 'bold' }}>{label}</Typography>
            <span style={{ ...valueBox, fontSize: 14, padding: '4px 8px', flex: 1 }}>{value}</span>
            <Button size="small" style={{ marginInlineStart: 8 }} onClick={() => copy(key, value)}>{copied === key ? tr('copied') : tr('copy')}</Button>
        </Box>
    );

    const renderGuides = () => (
        <Box style={{ marginTop: 12 }}>
            <Typography style={{ fontWeight: 'bold' }}>{tr('step1.guidesTitle')}</Typography>
            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '8px 0' }}>
                {GUIDES.map((g) => (
                    <Button key={g} variant={openGuide === g ? 'contained' : 'outlined'} size="small" onClick={() => setOpenGuide(openGuide === g ? '' : g)}>
                        {tr(`guides.${g}.title`)}
                    </Button>
                ))}
            </Box>
            {openGuide && (
                <Box style={{ background: 'var(--pls-d-surface-2, #fafafa)', border: '1px solid var(--pls-d-border-subtle, #eee)', borderRadius: 6, padding: '10px 16px' }}>
                    {tr(`guides.${openGuide}.note`) && <Typography style={{ color: 'var(--pls-d-text-2, #555)', marginBottom: 6 }}>{tr(`guides.${openGuide}.note`)}</Typography>}
                    <ol style={{ margin: 0, paddingInlineStart: 22, lineHeight: 1.8 }}>
                        {((t(`settings.sso.guides.${openGuide}.steps`, { returnObjects: true }) as any) || []).map((step: string, i: number) => (
                            <li key={i}><Typography component="span">{renderWithValues(step)}</Typography></li>
                        ))}
                    </ol>
                    <Typography style={{ marginTop: 8 }}><b>{tr('step1.then')}</b> {renderWithValues(tr(`guides.${openGuide}.after`))}</Typography>
                </Box>
            )}
        </Box>
    );

    const renderStep1 = () => (
        <Box style={box}>
            <Typography variant="h6">{tr('step1.title')}</Typography>
            <Typography style={{ marginBottom: 6 }}>{tr('step1.text')}</Typography>
            {copyRow('entity', tr('step1.entityId'), data.EntityId)}
            {copyRow('acs', tr('step1.acsUrl'), data.AcsUrl)}
            {copyRow('meta', tr('step1.spMetadata'), data.SpMetadataUrl)}
            <Typography style={{ color: 'var(--pls-d-text-2, #555)', fontSize: 13 }}>{tr('step1.nameIdHint')}</Typography>
            {renderGuides()}
        </Box>
    );

    const renderStep2 = () => (
        <Box style={box}>
            <Typography variant="h6">{tr('step2.title')}</Typography>
            <Typography style={{ marginBottom: 8 }}>{tr('step2.text')}</Typography>
            {!readOnly && (
                <>
                    <Box style={{ display: windowSize !== 'xs' ? 'flex' : 'block', alignItems: 'center' }}>
                        <TextField variant="outlined" size="small" value={link} placeholder="https://..." onChange={(e) => setLink(e.target.value)}
                            inputProps={{ style: { direction: 'ltr' } }} style={{ flex: 1, marginInlineEnd: 10, minWidth: 280 }} />
                        {button(tr('step2.readLink'), onReadLink, link.trim() === '')}
                    </Box>
                    <Typography style={{ marginTop: 10 }}>
                        {tr('step2.orFile')}{' '}
                        <input type="file" accept=".xml,text/xml,application/xml" onChange={onFile} disabled={busy} />
                    </Typography>
                </>
            )}
            <Box style={{ marginTop: 12 }}>
                {data.IdpSsoUrl ? (
                    <>
                        <Typography style={{ fontWeight: 'bold', color: 'var(--pls-d-success, #1b7f3b)' }}>{tr('step2.connected')}</Typography>
                        <Typography>{tr('step2.idpEntity')}: <span style={valueBox}>{data.IdpEntityId}</span></Typography>
                        <Typography>{tr('step2.idpLogin')}: <span style={valueBox}>{data.IdpSsoUrl}</span></Typography>
                        <Typography>
                            {tr('step2.certValid')}: <b>{data.CertNotAfter ? moment(data.CertNotAfter).format('DD/MM/YYYY') : '-'}</b>{' - '}
                            {data.IdpMetadataUrl ? tr('step2.certAuto') : tr('step2.certManual')}
                        </Typography>
                    </>
                ) : (
                    <Typography style={{ color: 'var(--pls-d-text-2, #777)' }}>{tr('step2.notConnected')}</Typography>
                )}
            </Box>
        </Box>
    );

    const rowError = (code: number) => tr(`step3.rowError${code === -1 ? 'Duplicate' : code === -3 ? 'OwnLocked' : 'NotFound'}`);

    const renderStep3 = () => (
        <Box style={box}>
            <Typography variant="h6">{tr('step3.title')}</Typography>
            <Typography style={{ marginBottom: 8 }}>{tr('step3.text')}</Typography>
            <Box style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640 }}>
                    <thead>
                        <tr>
                            <th style={cell}>{tr('step3.colUser')}</th>
                            <th style={cell}>{tr('step3.colEmail')}</th>
                            <th style={cell}>{tr('step3.colNameId')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data.Users || []).map((u: any) => {
                            // connected by the user themselves: read-only, so an admin edit can't silently replace it - only "Remove" (then Save)
                            const lockedByUser = !!(u.ConnectedByUser && u.SsoNameId && names[u.UserName] === u.SsoNameId);
                            return (
                            <tr key={u.UserName}>
                                <td style={cell}>
                                    <b>{u.UserName}</b>
                                    <div style={{ color: 'var(--pls-d-text-2, #777)', fontSize: 12 }}>{u.UserType === 'Main login' ? tr('step3.typeMain') : tr('step3.typeSub')}</div>
                                </td>
                                <td style={{ ...cell, direction: 'ltr' }}>{u.Email}</td>
                                <td style={cell}>
                                    <TextField variant="outlined" size="small" fullWidth value={names[u.UserName] || ''} disabled={readOnly || lockedByUser}
                                        placeholder={u.Email ? tr('step3.placeholderEmail', { email: u.Email }) : ''}
                                        inputProps={{ style: { direction: 'ltr' }, maxLength: 256 }}
                                        onChange={(e) => setNames({ ...names, [u.UserName]: e.target.value })} />
                                    {rowErrors[u.UserName] !== undefined && <div style={{ color: 'var(--pls-d-danger, #c00)', fontSize: 12 }}>{rowError(rowErrors[u.UserName])}</div>}
                                    {lockedByUser && (
                                        <div style={{ color: 'var(--pls-d-success, #1b7f3b)', fontSize: 12 }}>
                                            {tr('step3.connectedByUser', { date: u.ConnectedDate ? moment(u.ConnectedDate).format('DD/MM/YYYY') : '' })}
                                            {!readOnly && (
                                                <Button size="small" style={{ marginInlineStart: 8, padding: 0, minWidth: 0, fontSize: 12, textTransform: 'none', textDecoration: 'underline' }}
                                                    disabled={busy} onClick={() => setNames({ ...names, [u.UserName]: '' })}>
                                                    {tr('step3.removeConnection')}
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                    {u.ConnectedByUser && u.SsoNameId && !lockedByUser && (
                                        <div style={{ color: 'var(--pls-d-warning, #8a5a00)', fontSize: 12 }}>{tr('step3.removeNote')}</div>
                                    )}
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </Box>
            {!readOnly && (
                <Box style={{ marginTop: 8 }}>
                    {button(tr('step3.fillEmails'), onFillEmails)}
                    {button(tr('step3.save'), onSaveUsers)}
                </Box>
            )}
        </Box>
    );

    const modeOption = (value: string, disabled = false) => (
        <FormControlLabel value={value} disabled={busy || disabled || readOnly} control={<Radio color="primary" />} style={{ alignItems: 'flex-start', margin: '4px 0' }}
            label={<Box style={{ paddingTop: 9 }}>
                <Typography style={{ fontWeight: 'bold' }}>{tr(`step4.mode${value}`)}</Typography>
                <Typography style={{ color: 'var(--pls-d-text-2, #555)', fontSize: 14 }}>{tr(`step4.mode${value}Desc`)}</Typography>
            </Box>} />
    );

    const renderStep4 = () => {
        const ssoOnlyBlocked = !data.CanEnableSsoOnly && data.Mode !== 2;
        return (
            <Box style={box}>
                <Typography variant="h6">{tr('step4.title')}</Typography>
                <RadioGroup value={mode} onChange={(e: any) => setMode(e.target.value)}>
                    {modeOption('0')}
                    {modeOption('1')}
                    {modeOption('2', ssoOnlyBlocked)}
                </RadioGroup>
                {!readOnly && (
                    <>
                        <Typography style={{ margin: '6px 0', color: ssoOnlyBlocked ? 'var(--pls-d-warning, #8a5a00)' : 'var(--pls-d-success, #1b7f3b)', fontSize: 14 }}>
                            {ssoOnlyBlocked ? tr('step4.testLoginNeeded') : tr('step4.testLoginDone')}
                        </Typography>
                        {button(tr('step4.save'), onSaveMode, `${data.Mode}` === mode)}
                    </>
                )}
                <Typography style={{ marginTop: 10 }}>{tr('step4.howToLogin', { url: data.LoginPageUrl })}</Typography>
                <Typography style={{ color: 'var(--pls-d-text-2, #555)', fontSize: 13, marginTop: 4 }}>{tr('step4.emergency')}</Typography>
            </Box>
        );
    };

    /** Main account with sub-accounts: use this company login for the sub-accounts too (those without their own). */
    const renderShare = () => (
        <Box style={box}>
            <Typography variant="h6">{tr('share.title')}</Typography>
            <FormControlLabel
                style={{ alignItems: 'flex-start', margin: '6px 0' }}
                control={<Checkbox color="primary" checked={!!data.ShareWithSubAccounts} disabled={busy || readOnly || !data.IdpSsoUrl}
                    onChange={(e: any) => setConfirmShare(e.target.checked)} />}
                label={<Box style={{ paddingTop: 9 }}>
                    <Typography style={{ fontWeight: 'bold' }}>{tr('share.label', { count: data.SubAccountCount })}</Typography>
                    <Typography style={{ color: 'var(--pls-d-text-2, #555)', fontSize: 14 }}>{tr('share.text')}</Typography>
                </Box>} />
            {!data.IdpSsoUrl && <Typography style={{ color: 'var(--pls-d-warning, #8a5a00)', fontSize: 14 }}>{tr('share.needsIdp')}</Typography>}
        </Box>
    );

    const renderShareConfirm = () => (
        <BaseDialog classes={classes} open={confirmShare !== null} title={tr('share.title')}
            onClose={() => setConfirmShare(null)} onCancel={() => setConfirmShare(null)}
            onConfirm={() => { const share = !!confirmShare; setConfirmShare(null); onSaveShare(share); }}>
            <Typography>{tr(confirmShare ? 'share.confirmOn' : 'share.confirmOff', { count: data?.SubAccountCount })}</Typography>
        </BaseDialog>
    );

    const onSaveShare = async (share: boolean) => {
        const payload = await call(saveSsoShare({ Share: share }));
        if (payload) { apply(payload.Data); setMessage(tr(share ? 'share.savedOn' : 'share.savedOff')); }
    };

    const renderAttempts = () => (
        <Box style={box}>
            <Typography variant="h6">{tr('attempts.title')}</Typography>
            {(data.Attempts || []).length === 0 ? <Typography style={{ color: 'var(--pls-d-text-2, #777)' }}>{tr('attempts.none')}</Typography> : (
                <Box style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640, fontSize: 13 }}>
                        <thead>
                            <tr>
                                <th style={cell}>{tr('attempts.colDate')}</th>
                                <th style={cell}>{tr('attempts.colNameId')}</th>
                                <th style={cell}>{tr('attempts.colUser')}</th>
                                <th style={cell}>{tr('attempts.colResult')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.Attempts.map((a: any, i: number) => (
                                <tr key={i}>
                                    <td style={cell}>{moment(a.CreatedDate).format('DD/MM/YYYY HH:mm')}</td>
                                    <td style={{ ...cell, direction: 'ltr' }}>{a.SsoNameId}</td>
                                    <td style={cell}>{a.UserName}</td>
                                    <td style={{ ...cell, color: a.Result === 'Success' ? 'var(--pls-d-success, #1b7f3b)' : 'var(--pls-d-danger, #c00)' }}>
                                        {tr(`attempts.results.${a.Result}`) === `settings.sso.attempts.results.${a.Result}` ? a.Result : tr(`attempts.results.${a.Result}`)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Box>
            )}
        </Box>
    );

    return (
        <DefaultScreen currentPage="settings" subPage="ssoSettings" key="ssoSettings" classes={classes} containerClass={clsx(classes.management, classes.mb50)}>
            <Box className={'topSection'}>
                <Box className={clsx('head')}>
                    <Title
                        classes={classes}
                        isIcon={false}
                        ContainerStyle={{ padding: windowSize === 'xs' ? '10px 15px' : '11px 31.69px 8px 31.69px' }}
                        Element={
                            <Box className={clsx(classes.dFlex)} alignItems="center">
                                <MdVpnKey size={24} style={{ marginInlineEnd: 10 }} />
                                <Typography className={clsx(classes.managementTitle, 'mgmtTitle')} style={{ width: 'auto' }}>{tr('title')}</Typography>
                            </Box>
                        }
                    />
                </Box>
            </Box>
            <Box style={{ padding: windowSize === 'xs' ? '10px 15px' : '10px 32px', direction: isRTL ? 'rtl' : 'ltr' }}>
                <Box style={helpBox}><Typography>{tr('help')}</Typography></Box>
                {readOnly && (
                    <Box style={{ ...helpBox, background: 'var(--pls-d-warning-bg, #fff8e5)', borderColor: 'var(--pls-d-warning, #f0d58c)', marginTop: 12 }}>
                        <Typography style={{ fontWeight: 'bold' }}>{tr('readOnly.notice', { user: mainLogin })}</Typography>
                    </Box>
                )}
                {message && <Typography style={{ color: 'var(--pls-d-success, #1b7f3b)', fontWeight: 'bold', marginTop: 12 }}>{message}</Typography>}
                {error && <Typography style={{ color: 'var(--pls-d-danger, #c00)', fontWeight: 'bold', marginTop: 12 }}>{error}</Typography>}
                {data && data.SharedByMainAccount && (
                    <Box style={{ ...helpBox, background: 'var(--pls-d-success-bg, #eef8f0)', borderColor: 'var(--pls-d-success, #b7dfc0)', marginTop: 12 }}>
                        <Typography style={{ fontWeight: 'bold' }}>{tr('share.usingMain', { name: data.MainSubAccountName || '' })}</Typography>
                        <Typography style={{ fontSize: 14, marginTop: 4 }}>{tr('share.usingMainText')}</Typography>
                    </Box>
                )}
                {/* A sub-account using the main account's login has nothing to set up (the menu hides this page there) */}
                {data && !data.SharedByMainAccount && !data.HasConfig && (
                    <Box style={box}>
                        <Typography variant="h6">{tr('start.title')}</Typography>
                        <Typography style={{ margin: '6px 0 10px 0' }}>{readOnly ? tr('readOnly.notStarted') : tr('start.text')}</Typography>
                        {!readOnly && button(tr('start.button'), onStart)}
                    </Box>
                )}
                {data && !data.SharedByMainAccount && data.HasConfig && (
                    <>
                        {renderStep1()}
                        {renderStep2()}
                        {renderStep3()}
                        {renderStep4()}
                        {data.IsMainAccount && data.SubAccountCount > 0 && renderShare()}
                        {renderAttempts()}
                    </>
                )}
                {renderShareConfirm()}
            </Box>
        </DefaultScreen>
    );
};

export default SsoSettings;
